import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Button, Input, useToast } from '@centraoneplus/ui';
import { useAuthStore } from '@stores/authStore';
import { platformAuthService } from '@services/platform-auth.service';

// Mirrors the backend rule so we fail fast client-side with a clear message.
const STRONG_PASSWORD =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).+$/;

/**
 * Self-service account page for the logged-in platform user (super admin).
 * Currently supports changing the login email. Because the email is the login
 * identity and the active token embeds the old address, a successful change
 * forces a re-login (chosen behaviour) to avoid a stale-token refresh failure.
 */
export default function PlatformProfilePage() {
  const toast = useToast();
  const { user, logout } = useAuthStore();

  const [email, setEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Email change is verified: step 1 emails a code to the new address, step 2
  // confirms the code before the change is applied.
  const [codeSent, setCodeSent] = useState(false);
  const [emailChangeToken, setEmailChangeToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [maskedNewEmail, setMaskedNewEmail] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const me = await platformAuthService.getCurrentUser();
        if (!active) return;
        setEmail(me.email);
        setOriginalEmail(me.email);
        setRole(me.platformRole);
      } catch {
        // Fall back to the store so the page still renders offline.
        setEmail(user?.email || '');
        setOriginalEmail(user?.email || '');
        setRole(user?.platformRole || '');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmed = email.trim();
  const changed = trimmed.toLowerCase() !== originalEmail.trim().toLowerCase();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);

  // Step 1: send a verification code to the new email.
  const handleRequestCode = async () => {
    if (!emailValid) {
      toast.error('Validation Error', 'Please enter a valid email address');
      return;
    }
    if (!changed) return;

    try {
      setSaving(true);
      const res = await platformAuthService.requestEmailChange(trimmed);
      setEmailChangeToken(res.emailChangeToken);
      setMaskedNewEmail(res.maskedEmail || trimmed);
      setOtpCode('');
      setCodeSent(true);
      toast.success('Verification code sent', `We emailed a code to ${res.maskedEmail || trimmed}.`);
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; error?: { message?: string } }>;
      const status = axiosErr.response?.status;
      if (status === 409) {
        toast.error('Email in use', 'Another platform user already uses this email.');
      } else {
        toast.error(
          'Error',
          axiosErr.response?.data?.error?.message ||
            axiosErr.response?.data?.message ||
            'Could not send the verification code.',
        );
      }
    } finally {
      setSaving(false);
    }
  };

  // Step 2: verify the code and apply the change, then force re-login.
  const handleVerifyCode = async () => {
    if (otpCode.length !== 6) return;
    try {
      setVerifying(true);
      await platformAuthService.verifyEmailChange(emailChangeToken, otpCode);
      toast.success('Email updated', 'Please sign in again with your new email.');
      await logout();
      // Full document load: the identity is changing, so the module graph
      // must not carry this session's stores into the next one.
      window.location.assign('/platform-admin');
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; error?: { code?: string; message?: string } }>;
      const status = axiosErr.response?.status;
      const code = axiosErr.response?.data?.error?.code;
      if (status === 401 || code?.startsWith('OTP')) {
        toast.error('Incorrect code', 'That code is invalid or expired. Please try again.');
      } else if (status === 409) {
        toast.error('Email in use', 'Another platform user already uses this email.');
      } else {
        toast.error(
          'Error',
          axiosErr.response?.data?.error?.message ||
            axiosErr.response?.data?.message ||
            'Could not verify the code.',
        );
      }
    } finally {
      setVerifying(false);
    }
  };

  const cancelEmailChange = () => {
    setCodeSent(false);
    setEmailChangeToken('');
    setOtpCode('');
    setMaskedNewEmail('');
    setEmail(originalEmail);
  };

  const canSubmitPassword =
    !!currentPassword && !!newPassword && !!confirmPassword && !changingPassword;

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Validation Error', 'New password and confirmation do not match');
      return;
    }
    if (!STRONG_PASSWORD.test(newPassword) || newPassword.length < 8) {
      toast.error(
        'Weak password',
        'Password must be at least 8 characters and include upper, lower, number, and a special character.',
      );
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('Validation Error', 'New password must be different from the current one');
      return;
    }

    try {
      setChangingPassword(true);
      await platformAuthService.changePassword(currentPassword, newPassword);
      toast.success('Password changed', 'Your password has been updated.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; error?: { message?: string } }>;
      const status = axiosErr.response?.status;
      if (status === 401) {
        toast.error('Incorrect password', 'Your current password is incorrect.');
      } else {
        toast.error(
          'Error',
          axiosErr.response?.data?.error?.message ||
            axiosErr.response?.data?.message ||
            'Failed to change password.',
        );
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Account</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage your platform administrator account
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Email + role */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@centraone.com"
                disabled={codeSent}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This is your login email. We'll email a verification code to the new address before
                changing it, then sign you out to sign back in with the new email.
              </p>
            </div>

            {codeSent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Verification code
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  Enter the 6-digit code sent to{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-200">{maskedNewEmail}</span>.
                </p>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-xl tracking-widest"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-200">
                <ShieldCheck className="w-4 h-4" />
                {role || '—'}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              {!codeSent ? (
                <Button onClick={handleRequestCode} disabled={saving || !changed || !emailValid}>
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send verification code'
                  )}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={cancelEmailChange}
                    className="dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleVerifyCode} disabled={verifying || otpCode.length !== 6}>
                    {verifying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify & update'
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Change password */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-5">
            <div>
              <h3 className="text-base font-medium text-gray-900 dark:text-white">Change password</h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter your current password and choose a new one. You stay signed in after changing it.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Current password
              </label>
              <Input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New password
              </label>
              <Input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                At least 8 characters with upper &amp; lower case, a number, and a special character.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm new password
              </label>
              <Input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleChangePassword} disabled={!canSubmitPassword}>
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
